import { DollarSign, MapPin, Image as ImageIcon } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/use-mobile';

interface ComposerActionSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	canOffer: boolean;
	canMeetup: boolean;
	onMakeOffer: () => void;
	onProposeMeetup: () => void;
	onAttachPhoto: () => void;
	hint?: string;
}

interface Row {
	id: string;
	icon: React.ReactNode;
	label: string;
	sublabel?: string;
	disabled?: boolean;
	onClick: () => void;
}

export const ComposerActionSheet = ({
	open,
	onOpenChange,
	canOffer,
	canMeetup,
	onMakeOffer,
	onProposeMeetup,
	onAttachPhoto,
	hint,
}: ComposerActionSheetProps) => {
	const isMobile = useIsMobile();

	const rows: Row[] = [
		{
			id: 'offer',
			icon: <DollarSign className="h-5 w-5" />,
			label: 'Make an offer',
			sublabel: canOffer ? 'Send a price' : 'Pick an item from a listing first',
			disabled: !canOffer,
			onClick: onMakeOffer,
		},
		{
			id: 'meetup',
			icon: <MapPin className="h-5 w-5" />,
			label: 'Propose local pickup',
			sublabel: canMeetup ? 'Pick a time and place' : 'Available once an offer is accepted',
			disabled: !canMeetup,
			onClick: onProposeMeetup,
		},
		{
			id: 'photo',
			icon: <ImageIcon className="h-5 w-5" />,
			label: 'Send a photo',
			onClick: onAttachPhoto,
		},
	];

	const body = (
		<>
			{hint && (
				<div className="text-[12px] text-muted-foreground -mt-1 mb-2 truncate">{hint}</div>
			)}
			<div className="py-2 space-y-1">
				{rows.map((r) => (
					<button
						key={r.id}
						type="button"
						onClick={() => {
							if (r.disabled) return;
							onOpenChange(false);
							r.onClick();
						}}
						disabled={r.disabled}
						className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.05] transition-colors text-left disabled:opacity-50"
					>
						<div className="h-10 w-10 rounded-full bg-white/[0.06] grid place-items-center text-foreground shrink-0">
							{r.icon}
						</div>
						<div className="flex-1 min-w-0">
							<div className="text-[14px] font-medium text-foreground">{r.label}</div>
							{r.sublabel && (
								<div className="text-[12px] text-muted-foreground truncate">{r.sublabel}</div>
							)}
						</div>
					</button>
				))}
			</div>
		</>
	);

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="bottom" className="bg-chrome border-t border-white/[0.08] max-h-[60dvh]">
					<SheetHeader className="text-left">
						<SheetTitle>Quick actions</SheetTitle>
					</SheetHeader>
					{body}
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-chrome border-white/[0.08] max-w-sm">
				<DialogHeader>
					<DialogTitle>Quick actions</DialogTitle>
				</DialogHeader>
				{body}
			</DialogContent>
		</Dialog>
	);
};

export default ComposerActionSheet;
